import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import BuildIcon from '@mui/icons-material/Build';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import axios from 'axios';
import { API_URL } from '../../config';

const fmtSize = (bytes) => {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const fmtTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diff = Date.now() - d.getTime();
  const days = Math.floor(diff / 86400000);
  if (days < 1) {
    const hours = Math.floor(diff / 3600000);
    if (hours < 1) {
      const mins = Math.floor(diff / 60000);
      return mins < 1 ? 'just now' : `${mins}m ago`;
    }
    return `${hours}h ago`;
  }
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
};

const StatusBadge = ({ ok, label }) =>
  ok ? (
    <Chip
      icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
      label={label}
      size="small"
      color="success"
      variant="outlined"
    />
  ) : (
    <Chip
      icon={<ErrorIcon sx={{ fontSize: 14 }} />}
      label={label}
      size="small"
      color="warning"
      variant="outlined"
    />
  );

const DataArtifactsPanel = () => {
  const [bucket, setBucket] = useState(null);
  const [artifacts, setArtifacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [reloading, setReloading] = useState(false);
  const [flash, setFlash] = useState(null);

  const fetchArtifacts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/artifacts`);
      setBucket(data.bucket);
      setArtifacts(data.artifacts || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchArtifacts();
  }, [fetchArtifacts]);

  const sync = async (id) => {
    setBusyId(id);
    setFlash(null);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/artifacts/sync`, { id });
      setFlash({ severity: 'success', text: `Synced ${id} (${fmtSize(data.size_bytes)})` });
      await fetchArtifacts();
    } catch (e) {
      setFlash({
        severity: 'error',
        text: `Sync failed: ${e?.response?.data?.detail || e.message}`,
      });
    } finally {
      setBusyId(null);
    }
  };

  // Rebuild walks the source S3 data + writes back the derived artifact.
  // Sync runs inline in the request handler — fine for the current company
  // corpus (~5–25, runs in 5–15s). Watch for 29s API Gateway timeouts as
  // we grow.
  const rebuild = async (id) => {
    setBusyId(`rebuild:${id}`);
    setFlash(null);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/artifacts/rebuild`, { id });
      const parts = [
        `Rebuilt ${id} in ${data.duration_s}s`,
        data.family_count != null ? `${data.family_count} families` : null,
        data.total_jobs != null ? `${data.total_jobs} jobs` : null,
      ].filter(Boolean);
      setFlash({ severity: 'success', text: parts.join(' · ') });
      await fetchArtifacts();
    } catch (e) {
      setFlash({
        severity: 'error',
        text: `Rebuild failed: ${e?.response?.data?.detail || e.message}`,
      });
    } finally {
      setBusyId(null);
    }
  };

  const reloadAll = async () => {
    setReloading(true);
    setFlash(null);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/artifacts/reload`);
      setFlash({
        severity: 'success',
        text: `Reloaded caches: ${(data.caches || []).join(', ') || 'none'}`,
      });
    } catch (e) {
      setFlash({
        severity: 'error',
        text: `Reload failed: ${e?.response?.data?.detail || e.message}`,
      });
    } finally {
      setReloading(false);
    }
  };

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" color="text.secondary">
            S3 artifacts powering the UI. <code>{bucket}</code>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Cache reload only affects this Lambda instance. For a full refresh, redeploy the function.
          </Typography>
        </Box>
        <Button
          startIcon={<RefreshIcon />}
          onClick={reloadAll}
          disabled={reloading}
          size="small"
          variant="outlined"
        >
          {reloading ? 'Reloading…' : 'Reload caches'}
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {flash && (
        <Alert severity={flash.severity} sx={{ mb: 2 }} onClose={() => setFlash(null)}>
          {flash.text}
        </Alert>
      )}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Artifact</TableCell>
            <TableCell>S3</TableCell>
            <TableCell>Local (in-package)</TableCell>
            <TableCell align="right">Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {artifacts.map((a) => {
            const s3 = a.s3 || {};
            const local = a.local || {};
            return (
              <TableRow key={a.id} hover>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {a.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    s3://{a.s3_bucket}/{a.s3_key}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Served at <code>{a.endpoint}</code>
                  </Typography>
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <StatusBadge
                    ok={!!s3.exists}
                    label={s3.exists ? 'present' : s3.error ? 'error' : 'missing'}
                  />
                  {s3.exists && (
                    <Box sx={{ mt: 0.5 }}>
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        {fmtSize(s3.size_bytes)}
                      </Typography>
                      <Tooltip title={s3.modified_at || ''}>
                        <Typography variant="caption" color="text.secondary">
                          {fmtTime(s3.modified_at)}
                        </Typography>
                      </Tooltip>
                    </Box>
                  )}
                  {s3.error && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                      {s3.error}
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <StatusBadge
                    ok={!!local.available}
                    label={local.available ? 'available' : 'none'}
                  />
                  {local.available ? (
                    <Box sx={{ mt: 0.5 }}>
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        {fmtSize(local.size_bytes)}
                      </Typography>
                      <Tooltip title={local.modified_at || ''}>
                        <Typography variant="caption" color="text.secondary">
                          {fmtTime(local.modified_at)}
                        </Typography>
                      </Tooltip>
                    </Box>
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {local.reason || '—'}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                  <Stack direction="column" spacing={0.75} alignItems="flex-end">
                    <Tooltip
                      title={a.syncable ? 'Upload local file to S3 and clear cache' : 'No in-package source'}
                    >
                      <span>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<CloudUploadIcon />}
                          disabled={!a.syncable || busyId === a.id}
                          onClick={() => sync(a.id)}
                        >
                          {busyId === a.id ? 'Syncing…' : 'Sync'}
                        </Button>
                      </span>
                    </Tooltip>
                    {a.rebuildable && (
                      <Tooltip title="Walk S3 source data and rewrite this artifact in-process">
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={busyId === `rebuild:${a.id}` ? <CircularProgress size={12} /> : <BuildIcon />}
                            disabled={busyId === `rebuild:${a.id}`}
                            onClick={() => rebuild(a.id)}
                          >
                            {busyId === `rebuild:${a.id}` ? 'Rebuilding…' : (a.rebuild_label || 'Rebuild')}
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Paper>
  );
};

export default DataArtifactsPanel;
